import type * as React from 'react'
import { useCallback } from 'react'
import { AUTO_SAVE_DEBOUNCE_MS, useAutoSave } from '@/hooks/use-auto-save'
import { formatBigONotation } from '@/lib/format-complexity'
import { cn } from '@/lib/utils'
import { AutoSaveStatusIcon, autoSaveBorderClass } from './auto-save-status'
import { Input } from './input'

interface AutoSaveInputProps
  extends Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> {
  /** Current value of the input */
  value: string
  /** Called when the input value changes */
  onChange: (value: string) => void
  /** Called to save the value - should return a promise */
  onSave: (value: string) => Promise<void>
  /** Called after save completes with the (possibly transformed) value */
  onSaveComplete?: (transformedValue: string) => void
  /** Debounce delay in milliseconds before triggering save */
  debounceMs?: number
  /** Whether to format Big O notation with backticks on save */
  formatOnSave?: boolean
  /**
   * Send a scheduled-but-unsent save on unmount instead of dropping it. Opt in
   * where the field can be unmounted while the edit is still meant to survive
   * -- a collapsible panel, a route change.
   */
  flushOnUnmount?: boolean
}

/**
 * An input component that auto-saves with debouncing and shows save status.
 * Displays a small icon in the bottom-right corner:
 * - Gray save icon when typing (unsaved changes)
 * - Blue spinning loader when saving
 * - Green check when saved
 */
function AutoSaveInput({
  value,
  onChange,
  onSave,
  onSaveComplete,
  debounceMs = AUTO_SAVE_DEBOUNCE_MS.fast,
  formatOnSave = false,
  flushOnUnmount = false,
  className,
  ...props
}: AutoSaveInputProps) {
  const { state, onChange: scheduleSave } = useAutoSave({
    value,
    onSave,
    debounceMs,
    skipUnchanged: true,
    transform: formatOnSave ? formatBigONotation : undefined,
    onSaved: (savedValue, requestedValue) => {
      // Notify the parent only when formatting actually rewrote the input.
      if (savedValue !== requestedValue) {
        onSaveComplete?.(savedValue)
      }
    },
    flushOnUnmount,
    logLabel: 'AutoSaveInput',
  })

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value)
      scheduleSave(e.target.value)
    },
    [onChange, scheduleSave],
  )

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={handleChange}
        className={cn(
          'pr-8 transition-colors duration-200',
          autoSaveBorderClass(state),
          className,
        )}
        {...props}
      />
      <div className="pointer-events-none absolute right-2 bottom-1/2 translate-y-1/2">
        <AutoSaveStatusIcon state={state} className="h-3.5 w-3.5" />
      </div>
    </div>
  )
}

export { AutoSaveInput }
export type { AutoSaveInputProps }
